import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import { ArtworkImage, IMAGES, responsiveSourceSet } from './works';
import { LanguageService } from '../language.service';

gsap.registerPlugin(ScrollTrigger);

@Component({
  selector: 'app-works',
  templateUrl: './works.component.html',
  styleUrls: ['./works.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WorksComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class.animations-enabled')
  public readonly animationsEnabled = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  public images = IMAGES;
  private animationContext?: gsap.Context;
  private lightbox?: PhotoSwipeLightbox;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    public readonly language: LanguageService,
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    const reducedMotion = !this.animationsEnabled;
    const gallery = this.elementRef.nativeElement.querySelector<HTMLElement>('.gallery-grid');

    if (gallery) {
      this.lightbox = new PhotoSwipeLightbox({
        gallery,
        children: 'a.gallery-card',
        pswpModule: () => import('photoswipe'),
        bgOpacity: .96,
        showHideAnimationType: reducedMotion ? 'none' : 'zoom',
        wheelToZoom: true,
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        preload: [1, 2],
        paddingFn: viewport => ({
          top: viewport.x < 700 ? 56 : 72,
          bottom: viewport.x < 700 ? 56 : 72,
          left: viewport.x < 700 ? 12 : 72,
          right: viewport.x < 700 ? 12 : 72,
        }),
      });
      this.lightbox.on('uiRegister', () => {
        this.lightbox?.pswp?.ui?.registerElement({
          name: 'artwork-copyright',
          className: 'pswp__artwork-copyright',
          appendTo: 'root',
          order: 9,
          html: this.language.isEnglish
            ? '&copy; C&eacute;cile Binsztock &middot; All rights reserved'
            : '&copy; C&eacute;cile Binsztock &middot; Tous droits r&eacute;serv&eacute;s',
        });
      });
      this.lightbox.init();
    }

    if (reducedMotion) return;

    this.animationContext = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>('.gallery-card');
      const revealCards = (batch: Element[]): void => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 1.15,
          ease: 'power2.out',
          stagger: .13,
          overwrite: true,
        });
      };

      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .from('.eyebrow', { autoAlpha: 0, y: 16, duration: .55 })
        .from('.works-hero .title-line', {
          autoAlpha: 0,
          y: 42,
          duration: 1,
          stagger: .18,
        }, '-=.3')
        .from('.works-hero .title-script', {
          x: -18,
          duration: .85,
          ease: 'power2.out',
        }, '-=.78')
        .from('.hero-note', { autoAlpha: 0, x: 24, duration: .8 }, '-=.65')
        .from('.collection-line', { autoAlpha: 0, y: 12, duration: .55 }, '-=.35');

      ScrollTrigger.batch(cards, {
        start: 'top 90%',
        onEnter: revealCards,
        onEnterBack: revealCards,
      });

      cards.forEach(card => {
        const image = card.querySelector('img');
        if (!image) return;

        gsap.fromTo(image, { yPercent: -3 }, {
          yPercent: 3,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            start: 'top bottom',
            end: 'bottom top',
            scrub: .8,
          },
        });
      });

      // Browser history may restore the scroll position after the component
      // has initialized. Refresh once restoration has settled and reveal any
      // artwork already inside the viewport.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          ScrollTrigger.refresh();
          const visibleCards = cards.filter(card => {
            const bounds = card.getBoundingClientRect();
            return bounds.bottom > 0 && bounds.top < window.innerHeight * .95;
          });
          if (visibleCards.length) revealCards(visibleCards);
        });
      });
    }, this.elementRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.animationContext?.revert();
    this.lightbox?.destroy();
  }

  sourceSet(image: ArtworkImage): string {
    return responsiveSourceSet(image);
  }
}
